import React from "react"
import { useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message, Checkbox } from "@mimber/ui-web"
import {
  Form,
  InputField,
  SelectField,
  CurrencyInputField,
} from "@mimber/forms"
import { useCreateMembershipTier } from "@mimber/client/mimber-organization"
import { RecurrenceInterval } from "@mimber/schema"
import type { CreateMembershipTierFormProps } from "./CreateMembershipTierForm.types"
import {
  createMembershipTierSchema,
  type CreateMembershipTierData,
} from "./CreateMembershipTierForm.schema"

const INTERVAL_OPTIONS = [
  { label: "Daily", value: RecurrenceInterval.DAILY },
  { label: "Weekly", value: RecurrenceInterval.WEEKLY },
  { label: "Monthly", value: RecurrenceInterval.MONTHLY },
  { label: "Yearly", value: RecurrenceInterval.YEARLY },
]

export const CreateMembershipTierForm: React.FunctionComponent<
  CreateMembershipTierFormProps
> = ({ onSuccess }) => {
  const form = useForm<CreateMembershipTierData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(createMembershipTierSchema) as any,
    mode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      level: 1,
      price: undefined,
      intervalLength: undefined,
      intervalType: undefined,
      isDefault: false,
      isActive: true,
    },
  })

  const {
    control,
    setValue,
    watch,
    reset,
  } = form

  // Watch price field to conditionally show pricing fields
  const priceValue = useWatch({ control, name: "price" })
  const showPricingFields =
    priceValue !== undefined && priceValue !== null && priceValue > 0

  const mutation = useCreateMembershipTier({
    onSuccess: (response) => {
      message.success("Membership tier created successfully")
      reset() // Reset form to default values
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to create membership tier: ${error.message}`)
    },
  })

  const onSubmit = (data: CreateMembershipTierData) => {
    // Convert human-friendly form input to the DTO contract and ensure booleans have defaults.
    const processedData = {
      ...data,
      isDefault: data.isDefault ?? false,
      isActive: data.isActive ?? true,
    }
    mutation.mutate(processedData)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <InputField
        name="name"
        label="Tier Name"
        placeholder="e.g., Basic, Premium, VIP"
        required
        maxLength={255}
        showCount
      />

      <InputField
        name="description"
        label="Description"
        placeholder="Brief description of the tier benefits"
        maxLength={1000}
        showCount
        multiline
        rows={3}
      />

      <InputField
        name="level"
        label="Tier Level"
        placeholder="1"
        required
        help="Lower numbers indicate lower tier levels. Must be unique per organization."
      />

      <CurrencyInputField
        name="price"
        label="Price"
        minCents={0}
        help="Leave empty for free tiers."
      />

      {showPricingFields && (
        <>
          <InputField
            name="intervalLength"
            label="Billing Interval Length"
            placeholder="e.g., 1, 3, 6"
            required={showPricingFields}
            help="Number of intervals (e.g., 3 for 'every 3 months')"
          />

          <SelectField
            name="intervalType"
            label="Billing Interval Type"
            placeholder="Select interval type"
            required={showPricingFields}
            options={INTERVAL_OPTIONS}
            help="Frequency of recurring billing"
          />
        </>
      )}

      <Space orientation="vertical" size="small" style={{ marginBottom: 16 }}>
        <Checkbox
          checked={watch("isDefault")}
          onChange={(e) => setValue("isDefault", e.target.checked)}
        >
          Set as default tier for new members
        </Checkbox>
        <Checkbox
          checked={watch("isActive")}
          onChange={(e) => setValue("isActive", e.target.checked)}
        >
          Active (available for assignment)
        </Checkbox>
      </Space>

      <Space style={{ marginTop: 16 }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}

        >
          Create Tier
        </Button>
      </Space>
    </Form>
  )
}
